import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Image, Textbox } from "fabric";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { notifyError } from "../../../utils/notify";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg"];

const textColorOptions = [
  { label: "Czarny", value: "#000" },
  { label: "Szary", value: "#707070" },
  { label: "Biały", value: "#fff" },
  { label: "Złoty", value: "#C8A044" },
  { label: "Granatowy", value: "#1C3F58" },
];

function getObjectSnapshot(object) {
  if (!object) return null;

  const baseSnapshot = {
    left: object.left,
    top: object.top,
    scaleX: object.scaleX,
    scaleY: object.scaleY,
    angle: object.angle,
    width: object.width,
    height: object.height,
  };

  if (object.type === "textbox") {
    return {
      ...baseSnapshot,
      text: object.text,
      fontSize: object.fontSize,
      fontFamily: object.fontFamily,
      fontWeight: object.fontWeight,
      fontStyle: object.fontStyle,
      underline: object.underline,
      fill: object.fill,
      textAlign: object.textAlign,
      styles: object.styles,
    };
  }

  return baseSnapshot;
}

// Nazwa elementu na liście: etykieta z wzoru albo początek dodanego napisu
const getObjectLabel = (object) => {
  if (object.elementLabel) return object.elementLabel;
  if (object.type === "textbox") {
    const text = (object.text || "").trim().replace(/\s+/g, " ");
    return text ? `„${text.length > 28 ? `${text.slice(0, 28)}…` : text}”` : "Tekst";
  }
  return "Obraz";
};

const FONT_SIZE_STEP = 2;

const Settings = forwardRef(function Settings({
  canvas,
  selectedObject,
  defaultTextColor,
  onPickObject,
  onHistoryCommit,
  onUndo,
  onRedo,
  onResetLayout,
  canUndo = false,
  canRedo = false,
  expandSelection,
}, ref) {
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [fontSize, setFontSize] = useState("");
  const [fontFamily, setFontFamily] = useState("");
  const [color, setColor] = useState("");
  // Wymusza odświeżenie podświetlenia B/I/U i wyrównania po zmianie obiektu fabric
  const [, setFormatVersion] = useState(0);
  const selectedBaselineRef = useRef(null);
  const selectedObjectRef = useRef(null);

  const isCanvasReady = Boolean(canvas);
  const selectedLabel =
    selectedObject?.elementLabel ||
    (selectedObject?.type === "textbox" ? "Tekst" : "Obraz");

  const commitCanvasChange = () => {
    canvas?.renderAll();
    onHistoryCommit?.();
    setFormatVersion((version) => version + 1);
  };

  const isTextbox = selectedObject?.type === "textbox";
  const isBold = isTextbox && selectedObject.fontWeight === "bold";
  const isItalic = isTextbox && selectedObject.fontStyle === "italic";
  const isUnderline = isTextbox && Boolean(selectedObject.underline);
  const textAlign = isTextbox ? selectedObject.textAlign : null;
  const formatIconColor = (active) => (active ? "primary" : "disabled");

  useEffect(() => {
    if (!selectedObject) {
      clearSettings();
      selectedBaselineRef.current = null;
      selectedObjectRef.current = null;
      return;
    }

    if (selectedObjectRef.current !== selectedObject) {
      selectedBaselineRef.current = getObjectSnapshot(selectedObject);
      selectedObjectRef.current = selectedObject;
    }

    if (selectedObject.type === "textbox") {
      setWidth("");
      setHeight("");
      setFontSize(selectedObject.fontSize ?? "");
      setFontFamily(selectedObject.fontFamily ?? "");
      setColor(selectedObject.fill || "#000");
    } else if (selectedObject.type === "image") {
      setFontSize("");
      setFontFamily("");
      setColor("");
      setWidth(Math.round(selectedObject.width * selectedObject.scaleX));
      setHeight(Math.round(selectedObject.height * selectedObject.scaleY));
    }
  }, [selectedObject]);

  const clearSettings = () => {
    setWidth("");
    setHeight("");
    setFontSize("");
    setFontFamily("");
    setColor("");
  };

  const handleWidthChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    const intValue = parseInt(value, 10);

    setWidth(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 0) return;

    if (selectedObject.type === "textbox") {
      selectedObject.set({ width: intValue / selectedObject.scaleX });
      selectedObject.initDimensions();
    } else if (selectedObject.type === "image") {
      selectedObject.set({ scaleX: intValue / selectedObject.width });
    }

    commitCanvasChange();
  };

  const handleHeightChange = (e) => {
    const value = e.target.value.replace(/,/g, "");
    const intValue = parseInt(value, 10);

    setHeight(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 0) return;

    if (selectedObject.type === "textbox") {
      selectedObject.set({ height: intValue / selectedObject.scaleY });
    } else if (selectedObject.type === "image") {
      selectedObject.set({ scaleY: intValue / selectedObject.height });
    }

    commitCanvasChange();
  };

  const applyFontSize = (intValue) => {
    setFontSize(Number.isNaN(intValue) ? "" : intValue);
    if (!selectedObject || Number.isNaN(intValue) || intValue < 1) return;

    selectedObject.set({ fontSize: intValue });
    commitCanvasChange();
  };

  const handleFontSizeChange = (e) => {
    applyFontSize(parseInt(e.target.value.replace(/,/g, ""), 10));
  };

  // Przyciski − / + (szybciej niż wpisywanie, zwłaszcza na telefonie)
  const stepFontSize = (direction) => {
    const current = Math.round(Number(selectedObject?.fontSize) || Number(fontSize) || 0);
    applyFontSize(Math.max(1, current + direction * FONT_SIZE_STEP));
  };

  // Elementy, które da się zaznaczyć (bez ramki/tła)
  const pickableObjects = (canvas?.getObjects() || []).filter((object) => object.selectable !== false);

  // Zaznaczony fragment rozszerzony do całych pól (np. „19.10” → cała data mszy), żeby pogrubienie
  // czy pochylenie dało się zapisać we wzorze i przenieść na innych zmarłych
  const getStyleSelection = () => {
    const start = selectedObject.selectionStart;
    const end = selectedObject.selectionEnd;
    if (start === end || !expandSelection) return { start, end };
    const expanded = expandSelection(selectedObject, start, end);
    selectedObject.selectionStart = expanded.start;
    selectedObject.selectionEnd = expanded.end;
    return expanded;
  };

  const handleItalicChange = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const { start: selectionStart, end: selectionEnd } = getStyleSelection();

    if (selectionStart === selectionEnd) {
      const newValue = selectedObject.fontStyle === "italic" ? "normal" : "italic";
      selectedObject.set({ fontStyle: newValue });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ fontStyle: newValue }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const currentStyle =
        styles.length > 0 && styles[0].fontStyle === "italic" ? "italic" : "normal";
      const newStyle = currentStyle === "italic" ? "normal" : "italic";

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ fontStyle: newStyle }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const handleUnderlineChange = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const { start: selectionStart, end: selectionEnd } = getStyleSelection();

    if (selectionStart === selectionEnd) {
      const newValue = !selectedObject.underline;
      selectedObject.set({ underline: newValue });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const firstStyle = styles[0] || {};
      const newValue = !firstStyle.underline;

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const handleFontFamilyChange = (e) => {
    const newFontFamily = e.target.value;

    setFontFamily(newFontFamily);
    if (!selectedObject || !newFontFamily) return;

    selectedObject.set({ fontFamily: newFontFamily });
    commitCanvasChange();
  };

  const handleTextAlignChange = (value) => {
    if (!selectedObject || !value) return;

    selectedObject.set({ textAlign: value });
    commitCanvasChange();
  };

  const handleRemoveSelected = () => {
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      onHistoryCommit?.();
    }
  };

  const handleAddText = () => {
    if (!canvas) return;

    const newText = new Textbox("Nowy napis", {
      left: 50,
      top: 330,
      fontSize: 50,
      textAlign: "center",
      width: 300,
      fontWeight: 300,
      fill: defaultTextColor,
    });

    canvas.add(newText);
    canvas.setActiveObject(newText);
    commitCanvasChange();
  };

  const handleAddImage = (event) => {
    if (!canvas) return;

    const file = event.target.files[0];
    // Pozwala ponownie wybrać ten sam plik
    event.target.value = "";
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      notifyError("Wybierz obraz w formacie PNG lub JPG.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      notifyError("Obraz jest za duży – maksymalny rozmiar to 10 MB.");
      return;
    }

    const url = URL.createObjectURL(file);
    const imageElement = document.createElement("img");
    imageElement.src = url;
    imageElement.crossOrigin = "anonymous";

    imageElement.onload = () => {
      const imageWidth = imageElement.width;
      const imageHeight = imageElement.height;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const maxImageWidth = canvasWidth * 0.5;

      const scale = Math.min(maxImageWidth / imageWidth, canvasHeight / imageHeight);

      const fabricImage = new Image(imageElement, {
        top: 0,
        left: 0,
        scaleX: scale,
        scaleY: scale,
      });

      canvas.add(fabricImage);
      canvas.setActiveObject(fabricImage);
      commitCanvasChange();
      URL.revokeObjectURL(url);
    };
  };

  const handleBoldSelection = () => {
    if (!selectedObject || selectedObject.type !== "textbox") return;

    const { start: selectionStart, end: selectionEnd } = getStyleSelection();

    if (selectionStart === selectionEnd) {
      const newWeight = selectedObject.fontWeight === "bold" ? "normal" : "bold";
      selectedObject.set({ fontWeight: newWeight });
      for (let i = 0; i < selectedObject.text.length; i += 1) {
        selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
      }
    } else {
      const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
      const currentWeight =
        styles.length > 0 && styles[0].fontWeight === "bold" ? "bold" : "normal";
      const newWeight = currentWeight === "bold" ? "normal" : "bold";

      for (let i = selectionStart; i < selectionEnd; i += 1) {
        selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
      }
    }

    selectedObject.set({ dirty: true });
    commitCanvasChange();
  };

  const updateTextColor = (newColor) => {
    setColor(newColor);
    if (!selectedObject || selectedObject.type !== "textbox") return;

    selectedObject.set({ fill: newColor });
    commitCanvasChange();
  };

  const handleResetSelected = () => {
    if (!selectedObject || !selectedBaselineRef.current) return;

    selectedObject.set(selectedBaselineRef.current);
    if (selectedObject.type === "textbox") {
      selectedObject.initDimensions();
    }
    commitCanvasChange();
  };

  return (
    <aside ref={ref} className="settingsPanel">
      <div className="settingsPanelHeader">
        <div>
          <span className="settingsEyebrow">Narzędzia</span>
          <h2>{selectedObject ? selectedLabel : "Wybierz element"}</h2>
        </div>
        {selectedObject && (
          <button type="button" className="settingsBackToList" onClick={() => onPickObject?.(null)}>
            <ArrowBackIcon fontSize="small" />
            Lista elementów
          </button>
        )}
      </div>

      <div className="settingsQuickActions">
        <button className="customBtn secondary" onClick={onUndo} disabled={!canUndo}>
          <UndoIcon fontSize="small" />
          Cofnij
        </button>
        <button className="customBtn secondary" onClick={onRedo} disabled={!canRedo}>
          <RedoIcon fontSize="small" />
          Ponów
        </button>
      </div>

      {/* Lista elementów - zamiast samej wskazówki (małe napisy trudno trafić myszką i palcem) */}
      {!selectedObject && (
        <div className="settingsSection settingsElementList">
          <label>Kliknij element na projekcie lub wybierz z listy</label>
          {pickableObjects.map((object, index) => (
            <button
              key={object.elementId || `added-${index}`}
              type="button"
              className="settingsElementItem"
              onClick={() => onPickObject?.(object)}
            >
              {object.type === "textbox"
                ? <TextFieldsIcon fontSize="small" />
                : <ImageOutlinedIcon fontSize="small" />}
              <span>{getObjectLabel(object)}</span>
            </button>
          ))}
        </div>
      )}

      {selectedObject && selectedObject.type === "textbox" && (
        <div className="settingsSection">
          <label>Styl tekstu</label>
          <div className="btnsWrapperThree">
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("left")} className="btnWrapperDel" aria-label="Wyrównaj do lewej" aria-pressed={textAlign === "left"} title="Wyrównaj do lewej">
              <FormatAlignLeftIcon fontSize="inherit" color={formatIconColor(textAlign === "left")} className="icon" />
            </IconButton>
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("center")} className="btnWrapperDel" aria-label="Wyśrodkuj" aria-pressed={textAlign === "center"} title="Wyśrodkuj">
              <FormatAlignCenterIcon fontSize="inherit" color={formatIconColor(textAlign === "center")} className="icon" />
            </IconButton>
            <IconButton color="primary" size="large" onClick={() => handleTextAlignChange("right")} className="btnWrapperDel" aria-label="Wyrównaj do prawej" aria-pressed={textAlign === "right"} title="Wyrównaj do prawej">
              <FormatAlignRightIcon fontSize="inherit" color={formatIconColor(textAlign === "right")} className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleBoldSelection} aria-label="Pogrubienie" aria-pressed={isBold} title="Pogrubienie">
              <FormatBoldIcon fontSize="inherit" color={formatIconColor(isBold)} className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleItalicChange} aria-label="Kursywa" aria-pressed={isItalic} title="Kursywa">
              <FormatItalicIcon fontSize="inherit" color={formatIconColor(isItalic)} className="icon" />
            </IconButton>
            <IconButton className="btnWrapperDel" color="primary" onClick={handleUnderlineChange} aria-label="Podkreślenie" aria-pressed={isUnderline} title="Podkreślenie">
              <FormatUnderlinedIcon fontSize="inherit" color={formatIconColor(isUnderline)} className="icon" />
            </IconButton>
          </div>
          <label htmlFor="settingsFontSize">Rozmiar czcionki (px)</label>
          <div className="settingsStepper">
            <button type="button" onClick={() => stepFontSize(-1)} aria-label="Zmniejsz czcionkę" title="Zmniejsz">
              <RemoveIcon fontSize="small" />
            </button>
            <input id="settingsFontSize" type="number" min="1" value={fontSize} onChange={handleFontSizeChange} />
            <button type="button" onClick={() => stepFontSize(1)} aria-label="Powiększ czcionkę" title="Powiększ">
              <AddIcon fontSize="small" />
            </button>
          </div>

          <label htmlFor="fontFamily">Czcionka</label>
          <select
            name="fontFamily"
            id="fontFamily"
            onChange={handleFontFamilyChange}
            value={fontFamily}
            className="selectFont"
          >
            <option value="Arial" style={{ fontFamily: "Arial" }}>Arial</option>
            <option value="Helvetica" style={{ fontFamily: "Helvetica" }}>Helvetica</option>
            <option value="Times New Roman" style={{ fontFamily: "Times New Roman" }}>
              Times New Roman
            </option>
            <option value="Courier New" style={{ fontFamily: "Courier New" }}>
              Courier New
            </option>
            <option value="Bitter" style={{ fontFamily: "Bitter" }}>Bitter</option>
            <option value="DM Serif Display" style={{ fontFamily: "DM Serif Display" }}>
              DM Serif Display
            </option>
            <option value="Verdana" style={{ fontFamily: "Verdana" }}>Verdana</option>
            <option value="Georgia" style={{ fontFamily: "Georgia" }}>Georgia</option>
          </select>

          <label>Kolor tekstu</label>
          <div className="colorPickerGrid" role="radiogroup" aria-label="Kolor tekstu">
            {textColorOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`colorPickerOption${color === option.value ? " active" : ""}`}
                onClick={() => updateTextColor(option.value)}
                aria-pressed={color === option.value}
                aria-label={option.label}
                title={option.label}
              >
                <span
                  className="colorPickerSwatch"
                  style={{ backgroundColor: option.value }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedObject && selectedObject.type === "image" && (
        <div className="settingsSection">
          <label htmlFor="settingsImageWidth">Szerokość (px)</label>
          <input id="settingsImageWidth" type="number" min="1" value={width} onChange={handleWidthChange} />
          <label htmlFor="settingsImageHeight">Wysokość (px)</label>
          <input id="settingsImageHeight" type="number" min="1" value={height} onChange={handleHeightChange} />
        </div>
      )}

      <div className="settingsSection settingsAddSection">
        <label>Dodaj element</label>
        <button className="customBtn" onClick={handleAddText} disabled={!isCanvasReady}>
          Dodaj tekst
        </button>
        <input
          type="file"
          accept=".png,.jpg,.jpeg"
          id="fileInput"
          className="inputFile"
          onChange={handleAddImage}
          disabled={!isCanvasReady}
        />
        <label
          htmlFor="fileInput"
          className={`customBtn${!isCanvasReady ? " disabled" : ""}`}
        >
          Dodaj obraz
        </label>
      </div>

      {selectedObject && (
        <div className="settingsSection settingsDangerZone">
          <button className="customBtn secondary" onClick={handleResetSelected}>
            <RestartAltIcon fontSize="small" />
            Resetuj element
          </button>
          <button className="customBtn secondary danger" onClick={handleRemoveSelected}>
            <DeleteIcon fontSize="small" />
            Usuń element
          </button>
        </div>
      )}

      {/* Reset całego układu - rzadko potrzebny i nieodwracalny, więc jako mały link (z potwierdzeniem) */}
      <button type="button" className="settingsResetLayout" onClick={onResetLayout} disabled={!isCanvasReady}>
        <RestartAltIcon fontSize="small" />
        Przywróć układ wzoru (usuń wszystkie zmiany)
      </button>
    </aside>
  );
});

export default Settings;
